'use client';

import React, { useState } from 'react';
import { 
  X, 
  Clock, 
  ChevronDown, 
  User, 
  Scissors, 
  CreditCard, 
  CheckCircle2, 
  AlertCircle 
} from 'lucide-react';

interface Staff {
  id: string;
  name: string;
  roleTitle?: string;
}

interface Service {
  id: string;
  name: string;
  duration: number;
  cashPrice: number;
}

interface LooseAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  staffList: Staff[];
  services: Service[];
  onSubmitLooseAppointment: (payload: {
    staffId: string;
    time: string;
    serviceId: string;
    clientName: string;
    clientPhone: string;
    paymentChannel: string;
  }) => Promise<void>;
}

export default function LooseAppointmentModal({
  isOpen,
  onClose,
  staffList,
  services,
  onSubmitLooseAppointment
}: LooseAppointmentModalProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedStaffId, setSelectedStaffId] = useState(staffList[0]?.id || '');
  const [timeValue, setTimeValue] = useState('11:45 AM');
  
  // Paso 2
  const [selectedServiceId, setSelectedServiceId] = useState(services[0]?.id || '');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [paymentChannel, setPaymentChannel] = useState('EFECTIVO');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  if (!isOpen) return null;

  const selectedService = services.find(s => s.id === selectedServiceId) || services[0];

  const handleContinueStep1 = () => {
    if (!selectedStaffId) {
      setErrorMsg('Por favor seleccione qué profesional atendió.');
      return;
    }
    setErrorMsg('');
    setStep(2);
  };

  const handleFinish = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientName.trim()) {
      setErrorMsg('Ingrese el nombre del cliente.');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');
    try {
      await onSubmitLooseAppointment({
        staffId: selectedStaffId,
        time: timeValue,
        serviceId: selectedServiceId || services[0]?.id,
        clientName: clientName.trim(),
        clientPhone: clientPhone.trim() || '1100000000',
        paymentChannel
      });
      onClose();
      // Reset
      setStep(1);
      setClientName('');
      setClientPhone('');
    } catch (err: any) {
      setErrorMsg(err.message || 'Error registrando turno suelto.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl border border-slate-100 animate-in fade-in zoom-in-95 duration-150">
        
        {/* Header */}
        <div className="p-6 pb-4 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">
              Turno suelto
            </h2>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
              Un turno que no estaba agendado — entra como sobreturno y se cobra ahora. El cliente se elige en el paso siguiente.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Paso 1: Quién lo atendió y a qué hora */}
        {step === 1 && (
          <div className="p-6 space-y-5">
            {/* Campo 1: ¿QUIÉN LO ATENDIÓ? */}
            <div>
              <label className="block text-[11px] font-extrabold tracking-wider text-slate-500 uppercase mb-2">
                ¿Quién lo atendió?
              </label>
              <div className="relative">
                <select
                  value={selectedStaffId}
                  onChange={(e) => setSelectedStaffId(e.target.value)}
                  className="w-full appearance-none bg-slate-50/80 border-2 border-emerald-500/70 focus:border-emerald-600 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 transition cursor-pointer"
                >
                  <option value="" disabled>Elegí un profesional</option>
                  {staffList.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.name} {st.roleTitle ? `(${st.roleTitle})` : ''}
                    </option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-500 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            {/* Campo 2: ¿A QUÉ HORA SE HIZO? */}
            <div>
              <label className="block text-[11px] font-extrabold tracking-wider text-slate-500 uppercase mb-2">
                ¿A qué hora se hizo?
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={timeValue}
                  onChange={(e) => setTimeValue(e.target.value)}
                  placeholder="11:45 AM"
                  className="w-full bg-slate-50/80 border border-slate-200 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
                />
                <Clock className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
              <p className="text-xs text-slate-400 mt-2">
                No importa si se superpone con otro turno de ese profesional.
              </p>
            </div>

            {errorMsg && (
              <div className="p-3 rounded-xl bg-rose-50 text-rose-600 text-xs font-semibold flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            {/* Botón Continuar */}
            <div className="pt-2">
              <button
                type="button"
                onClick={handleContinueStep1}
                className="w-full py-3 bg-[#5ea897] hover:bg-[#4d9786] text-white text-sm font-bold rounded-2xl shadow-sm transition transform active:scale-95"
              >
                Continuar
              </button>
            </div>
          </div>
        )}

        {/* Paso 2: Servicio, Datos del Cliente y Canal de Cobro */}
        {step === 2 && (
          <form onSubmit={handleFinish} className="p-6 space-y-4">
            {/* Selección de Servicio */}
            <div>
              <label className="block text-[11px] font-extrabold tracking-wider text-slate-500 uppercase mb-1.5">
                Servicio Realizado
              </label>
              <select
                value={selectedServiceId}
                onChange={(e) => setSelectedServiceId(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:outline-none focus:border-emerald-500"
              >
                {services.map((srv) => (
                  <option key={srv.id} value={srv.id}>
                    {srv.name} — ${srv.cashPrice.toLocaleString('es-AR')}
                  </option>
                ))}
              </select>
            </div>

            {/* Nombre del Cliente */}
            <div>
              <label className="block text-[11px] font-extrabold tracking-wider text-slate-500 uppercase mb-1.5">
                Nombre del Cliente
              </label>
              <input
                type="text"
                required
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Ej. Lucas Fernández"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:outline-none focus:border-emerald-500"
              />
            </div>

            {/* Teléfono / WhatsApp */}
            <div>
              <label className="block text-[11px] font-extrabold tracking-wider text-slate-500 uppercase mb-1.5">
                WhatsApp (Opcional)
              </label>
              <input
                type="tel"
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value)}
                placeholder="Ej. 11 4455-6677"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:outline-none focus:border-emerald-500"
              />
            </div>

            {/* Medio de Pago */}
            <div>
              <label className="block text-[11px] font-extrabold tracking-wider text-slate-500 uppercase mb-1.5">
                Medio de Pago
              </label>
              <div className="grid grid-cols-3 gap-1.5">
                {['EFECTIVO', 'DEBITO', 'CREDITO', 'TRANSFERENCIA', 'MERCADOPAGO', 'OTRO'].map((ch) => (
                  <button
                    key={ch}
                    type="button"
                    onClick={() => setPaymentChannel(ch)}
                    className={`py-2 px-1 rounded-xl text-[11px] font-bold border transition text-center ${
                      paymentChannel === ch
                        ? 'bg-[#047857] border-[#047857] text-white shadow-sm'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    {ch}
                  </button>
                ))}
              </div>
            </div>

            {errorMsg && (
              <div className="p-3 rounded-xl bg-rose-50 text-rose-600 text-xs font-semibold flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition"
              >
                Atrás
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-2.5 bg-[#047857] hover:bg-[#065f46] text-white text-xs font-bold rounded-xl shadow-md transition disabled:opacity-50"
              >
                {submitting ? 'Registrando...' : `Cobrar $${(selectedService?.cashPrice || 0).toLocaleString('es-AR')}`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
